import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import { type MutableRefObject, useMemo, useCallback, useEffect, useRef } from 'react'
import type { GridApi, RowHeightParams } from 'ag-grid-community'
import { gridViewManager } from '@/utils/grid'
import { useUserSettings } from '@/hooks/settings'
import { useLocalStorage } from '@gravitate-js/the-armory'
import type moment from 'moment'
import type { Match, MatchOverviewResponse } from '../../../../api/useSupplierReconciliation/types/match'
import type { SupplierReconciliationMetadataResponse } from '../../../../api/useMetadata/types'
import { getMatchColumnDefs } from './columnDefs'
import { MatchDetailRenderer } from './MatchDetailRenderer'
import { BulkChangeBar } from './BulkChangeBar'
import { MatchViewToolbar } from './MatchViewToolbar'

const STORAGE_KEY = 'match-view-grid'

interface MatchViewGridProps {
  // Grid data and loading
  filteredData?: MatchOverviewResponse
  isLoading: boolean

  // Grid ref and selection
  gridAPIRef: MutableRefObject<GridApi<Match> | null>
  selectedRows: Match[]
  setSelectedRows: (rows: Match[]) => void

  // Server filters
  serverFilters: Record<string, unknown>
  setServerFilters: (filters: Record<string, unknown>) => void

  // Toolbar props
  dateRange: [Date?, Date?]
  setDateRange: (dateRange: [Date?, Date?]) => void
  // DateRangeFilter props
  dateFilters: { From: moment.Moment; To: moment.Moment }
  setDateFilters: (filters: { From: moment.Moment; To: moment.Moment }) => void
  dayRangeStorage: { back: number; ahead: number }
  setDayRangeStorage: (range: { back: number; ahead: number }) => void
  assignedToMe: boolean
  setAssignedToMe: (assignedToMe: boolean) => void
  // Tab state for dynamic slider range
  isVoidedViewActive: boolean
  activeStatus: string | null
  // Default helpers for reset functionality
  getDefaultApprovedDayRangeStorage: () => { back: number; ahead: number }
  getDefaultVoidedDayRangeStorage: () => { back: number; ahead: number }
  getDefaultOtherTabsDayRangeStorage: () => { back: number; ahead: number }

  // Action handlers
  handleApproveMatch: (match: Match, reasonCode?: string) => Promise<void>
  handleHoldMatch: (match: Match, reasonCode?: string) => Promise<void>
  handleUnholdMatch: (match: Match) => Promise<void>
  handleSearchMatch: (match: Match) => Promise<void>
  handleVoidInvoiceAction: (invoiceId: string) => Promise<void>
  handleUnvoidInvoiceAction: (invoiceId: string) => Promise<void>
  handleBulkCancel: () => void
  handleBulkApprove: (matchIds: string[], reasonCode?: string) => Promise<void>
  handleBulkHold: (matchIds: string[], reasonCode?: string) => Promise<void>
  handleMerge: (matchId: string, links: { matchIds: string[] }, isUnmatch?: boolean) => Promise<void>
  handleCreateInvoice: (invoiceData: { body: unknown }) => Promise<void>

  // Metadata and security
  metadata?: SupplierReconciliationMetadataResponse
  metadataLoading: boolean
  scopes: Record<string, boolean>

  // Timezone resolution
  getTimezoneForMatch?: (terminals?: string[]) => string | undefined

  // Column filter
  invoiceDueFilter?: string | null
}

export function MatchViewGrid({
  filteredData,
  isLoading,
  gridAPIRef,
  selectedRows,
  setSelectedRows,
  serverFilters,
  setServerFilters,
  dateRange,
  setDateRange,
  dateFilters,
  setDateFilters,
  dayRangeStorage,
  setDayRangeStorage,
  assignedToMe,
  setAssignedToMe,
  isVoidedViewActive,
  activeStatus,
  getDefaultApprovedDayRangeStorage,
  getDefaultVoidedDayRangeStorage,
  getDefaultOtherTabsDayRangeStorage,
  handleApproveMatch,
  handleHoldMatch,
  handleUnholdMatch,
  handleSearchMatch,
  handleVoidInvoiceAction,
  handleUnvoidInvoiceAction,
  handleBulkCancel,
  handleBulkApprove,
  handleBulkHold,
  handleMerge,
  handleCreateInvoice,
  metadata,
  metadataLoading,
  scopes,
  getTimezoneForMatch,
  invoiceDueFilter,
}: MatchViewGridProps) {
  const { updateSetting } = useUserSettings()

  // Check if grid configuration exists
  const { value: existingGridConfig } = useLocalStorage(`gridConfig::${STORAGE_KEY}`)

  // Memoize column definitions to prevent grid reinitialization
  const columnDefs = useMemo(() => {
    return getMatchColumnDefs({
      serverParams: filteredData?.params ? { params: filteredData.params } : undefined,
      getTimezoneForMatch,
    })
  }, [filteredData?.params, getTimezoneForMatch])

  // Custom grid config change handler to manage default column visibility
  const handleGridConfigChanged = useCallback(
    (config: any) => {
      // Guard against null/undefined config
      if (!config) return

      // Always save the configuration
      updateSetting(`gridConfig::${STORAGE_KEY}`, config)

      // If this is the first time and no previous config exists, set default visibility
      if (!existingGridConfig && config.column && gridAPIRef.current) {
        // Find ORDER # and PO # columns and hide them by default
        const updatedColumnState = config.column.map((colState: any) => {
          if (colState.colId === 'ORDER #' || colState.colId === 'PO #') {
            return { ...colState, hide: true }
          }
          return colState
        })

        // Apply the updated column state
        const updatedConfig = { ...config, column: updatedColumnState }

        // Update both local storage and the grid
        updateSetting(`gridConfig::${STORAGE_KEY}`, updatedConfig)

        // Apply to grid after a short delay to ensure it's ready
        setTimeout(() => {
          if (gridAPIRef.current?.columnApi) {
            gridAPIRef.current.columnApi.applyColumnState({
              state: updatedColumnState,
              applyOrder: true,
            })
          }
        }, 100)
      }
    },
    [existingGridConfig, updateSetting, gridAPIRef]
  )

  // Apply invoice due filter when grid is ready - improved reliability
  const applyInvoiceDueFilter = useCallback(() => {
    if (!gridAPIRef.current || invoiceDueFilter === null) {
      return
    }

    try {
      const filterInstance = gridAPIRef.current?.getFilterInstance('days_until_due')

      if (filterInstance) {
        if (invoiceDueFilter === 'overdue') {
          // Apply overdue filter (less than or equal to 0 days until due)
          const filterModel = {
            filterType: 'number',
            type: 'lessThanOrEqual',
            filter: 0,
          }

          filterInstance.setModel(filterModel)
          gridAPIRef.current.onFilterChanged()
        } else {
          // Clear the filter
          filterInstance.setModel(null)
          gridAPIRef.current.onFilterChanged()
        }
      }
    } catch (error) {
      console.error('Filter application failed:', error)
    }
  }, [invoiceDueFilter])

  // Note: Filter application logic consolidated into data monitoring effect below for single-application approach

  // Apply invoice due filter after data loads (single-application approach)
  useEffect(() => {
    // Apply filter only when: grid is ready, filter is needed, data is loaded, and not currently loading
    if (gridAPIRef.current && invoiceDueFilter === 'overdue' && filteredData?.rows?.length && !isLoading) {
      // Small delay to ensure data is fully processed by the grid
      setTimeout(() => {
        try {
          const filterInstance = gridAPIRef.current?.getFilterInstance('days_until_due')
          if (filterInstance) {
            const isActive = filterInstance.isFilterActive()

            if (!isActive && invoiceDueFilter === 'overdue') {
              applyInvoiceDueFilter()
            }
          }
        } catch (error) {
          console.error('Error during filter application:', error)
        }
      }, 100) // Allow grid to process the data first
    }
  }, [filteredData?.rows, invoiceDueFilter, applyInvoiceDueFilter, isLoading])

  // Memoize agPropOverrides to prevent grid reinitialization on every render
  // This is critical for maintaining column visibility settings
  const agPropOverrides = useMemo(
    () => ({
      context: {
        scopes,
        onApproveMatch: handleApproveMatch,
        onHoldMatch: handleHoldMatch,
        onUnholdMatch: handleUnholdMatch,
        onSearchMatch: handleSearchMatch,
        onVoidInvoice: handleVoidInvoiceAction,
        onUnvoidInvoice: handleUnvoidInvoiceAction,
        metadata: metadata,
        modelConfig: filteredData?.modelConfig,
      },
      defaultColDef: {
        wrapText: true,
        autoHeight: true,
      },
      domLayout: 'normal',
      rowSelection: 'multiple',
      rowMultiSelectWithClick: false,
      suppressRowClickSelection: true,
      enableRangeSelection: true,
      suppressColumnVirtualisation: false,
      suppressAnimationFrame: false,
      skipHeaderOnAutoSize: true,
      suppressAutoSize: true,
      maintainColumnOrder: true,
      suppressColumnMoveAnimation: false,
      suppressMovableColumns: false,
      // Prevent grid from changing sort when rows are selected/deselected
      suppressRowTransform: true,
      getRowId: (params) => {
        const data = params.data as Match

        // First try explicit match ID if it exists
        if (data?.id || data?.match_id || data?._id) {
          return String(data.id || data.match_id || data._id)
        }

        // Create composite key from invoice numbers and BOL numbers
        const invoiceIds = (data?.invoice_numbers || [])
          .map((inv: { id?: string; number?: string }) => inv.id || inv.number)
          .join(',')
        const bolNumbers = (data?.bol_numbers || [])
          .map((bol: { id?: string; number?: string }) => bol.id || bol.number)
          .join(',')

        if (invoiceIds || bolNumbers) {
          const invoicePart = invoiceIds || 'no-invoice'
          const bolPart = bolNumbers || 'no-bol'
          return `inv-${invoicePart}-bol-${bolPart}`
        }

        // Fallback if no invoice or BOL info - use deterministic hash instead of random
        console.warn('Row missing unique identifier - available fields:', Object.keys(data || {}))
        const fallbackData = JSON.stringify({
          supplier_id: data?.supplier_id,
          supplier_name: data?.supplier_name,
          created_at: data?.created_at,
          invoice_amount: data?.invoice_amount,
          bol_amount: data?.bol_amount,
        })
        // Create a simple hash for deterministic ID
        let hash = 0
        for (let i = 0; i < fallbackData.length; i++) {
          const char = fallbackData.charCodeAt(i)
          hash = (hash << 5) - hash + char
          hash = hash & hash // Convert to 32-bit integer
        }
        return `fallback-${data?.supplier_id || 'unknown'}-${Math.abs(hash).toString(36)}`
      },
      // Allow selection on all nodes
      isRowSelectable: () => true,

      // Basic master-detail configuration
      masterDetail: true,
      detailRowAutoHeight: true,
      isRowMaster: (data) => {
        // Only show expand button if there are multiple invoices OR multiple BOLs
        if (!data) return false
        const invoiceCount = data.invoice_numbers?.length || 0
        const bolCount = data.bol_numbers?.length || 0
        // Show expand if there's more than one invoice or more than one BOL
        return invoiceCount > 1 || bolCount > 1
      },

      detailCellRenderer: MatchDetailRenderer,
      detailCellRendererParams: {
        context: {
          scopes,
        },
        style: { padding: '8px 16px' },
      },

      // Customize row height based on content
      getRowHeight: (params: RowHeightParams<Match>) => {
        if (!params.data) return 40

        // Get counts of BOLs and invoices
        const bolCount = params.data.bol_numbers?.length || 0
        const invoiceCount = params.data.invoice_numbers?.length || 0

        // Get product count for wrapping calculation
        const productCount = params.data.products?.length || 0

        // Get product count for wrapping calculation
        const terminalCount = params.data.terminals?.length || 0

        // Get product count for wrapping calculation
        const destinationCount = params.data.destinations?.length || 0

        // Estimate how many rows of products based on typical tag width
        // Each product tag is ~60-80px, assume column width ~150px = ~2 tags per row
        const productRows = productCount > 0 ? Math.ceil(productCount / 2) : 1
        const terminalRows = terminalCount > 0 ? Math.ceil(terminalCount / 2) : 1
        const destinationRows = destinationCount > 0 ? Math.ceil(destinationCount / 2) : 1

        // Find the maximum count between BOLs, invoices, and product rows
        const maxItemCount = Math.max(bolCount, invoiceCount, productRows, terminalRows, destinationRows)

        // Base height is 40px, add 20px for each additional item beyond 1
        const additionalHeight = Math.max(0, maxItemCount - 1) * 20

        return 40 + additionalHeight
      },
    }),
    [
      scopes,
      handleApproveMatch,
      handleHoldMatch,
      handleUnholdMatch,
      handleSearchMatch,
      handleVoidInvoiceAction,
      handleUnvoidInvoiceAction,
      metadata,
      filteredData?.modelConfig,
      invoiceDueFilter,
    ]
  )

  return (
    <Vertical style={{ flex: 1, overflow: 'hidden' }}>
      {/* Grid View with Master-Detail */}
      <Vertical style={{ flex: 1 }}>
        <GraviGrid
          controlBarProps={{
            title: 'Match View',
            actionButtons: (
              <MatchViewToolbar
                dateRange={dateRange}
                setDateRange={setDateRange}
                dateFilters={dateFilters}
                setDateFilters={setDateFilters}
                dayRangeStorage={dayRangeStorage}
                setDayRangeStorage={setDayRangeStorage}
                assignedToMe={assignedToMe}
                setAssignedToMe={setAssignedToMe}
                isVoidedViewActive={isVoidedViewActive}
                activeStatus={activeStatus}
                getDefaultApprovedDayRangeStorage={getDefaultApprovedDayRangeStorage}
                getDefaultVoidedDayRangeStorage={getDefaultVoidedDayRangeStorage}
                getDefaultOtherTabsDayRangeStorage={getDefaultOtherTabsDayRangeStorage}
                gridAPIRef={gridAPIRef as MutableRefObject<GridApi<Match>>}
                handleCreateInvoice={handleCreateInvoice}
                metadata={metadata}
                metadataLoading={metadataLoading}
                filteredData={filteredData}
              />
            ),
            serverParams: filteredData?.params,
            // Always provide both filters and setFilters for query filters drawer functionality
            filters: serverFilters,
            setFilters: setServerFilters,
            rowCount: filteredData?.rows?.length || 0,
          }}
          columnDefs={columnDefs}
          rowData={filteredData?.rows || []}
          loading={isLoading}
          externalRef={gridAPIRef as MutableRefObject<GridApi<Match>>}
          storageKey={STORAGE_KEY}
          onGridConfigChanged={handleGridConfigChanged}
          onSelectionChanged={(event) => {
            // Use getSelectedRows() which automatically excludes group nodes
            const selectedData = event.api.getSelectedRows() as Match[]
            setSelectedRows(selectedData)
          }}
          onGridReady={(params) => {
            // Ensure the gridAPIRef is set (GraviGrid should handle this, but let's be safe)
            if (params.api && !gridAPIRef.current) {
              gridAPIRef.current = params.api
            }
          }}
          agPropOverrides={agPropOverrides}
          {...gridViewManager(STORAGE_KEY, updateSetting)}
        />
      </Vertical>

      {/* Bulk Action Bar - shows when rows are selected */}
      {selectedRows.length > 0 && (
        <BulkChangeBar
          selectedRows={selectedRows}
          onCancel={handleBulkCancel}
          onApprove={handleBulkApprove}
          onMatch={async (matchId, links, isUnmatch?: boolean) => {
            await handleMerge(matchId, links, isUnmatch)
          }}
          onMarkHold={handleBulkHold}
          metadata={metadata}
          metadataLoading={metadataLoading}
        />
      )}
    </Vertical>
  )
}
